// Festival Detail View - Overview page with info, schedule, and vendor preview
import React, { useState } from 'react';
import {
  ChevronLeft,
  MapPin,
  Calendar,
  ExternalLink,
  Star,
  Check,
  Map as MapIcon,
  Ticket,
  CalendarPlus,
  Bookmark,
  BookMarked,
  CheckCircle2,
  Navigation,
  PenLine,
  Plus,
  Trash2,
} from 'lucide-react';
import { Festival, Vendor, SAMPLE_VENDORS, FestivalNote } from '../data/festivals';

// Helper to generate calendar URLs
const generateCalendarLinks = (festival: Festival) => {
  const title = encodeURIComponent(festival.name);
  const location = encodeURIComponent(`${festival.venue || ''}, ${festival.address || festival.location}`);
  const description = encodeURIComponent(`${festival.description}\n\n${festival.website || ''}`);

  // Parse date - handle formats like "December 6, 2026" or "April 17-20, 2026"
  const dateStr = festival.date || '';
  let startDate = '';
  let endDate = '';

  // Try to parse the date
  const dateMatch = dateStr.match(/(\w+)\s+(\d+)(?:-(\d+))?,\s+(\d{4})/);
  if (dateMatch) {
    const [, month, startDay, endDay, year] = dateMatch;
    const monthNum = new Date(`${month} 1, 2000`).getMonth() + 1;
    const monthStr = monthNum.toString().padStart(2, '0');
    startDate = `${year}${monthStr}${startDay.padStart(2, '0')}`;
    endDate = endDay
      ? `${year}${monthStr}${endDay.padStart(2, '0')}`
      : startDate;
  }

  // Google Calendar
  const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&location=${location}&details=${description}`;

  // Apple Calendar (ICS file)
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${festival.name}
LOCATION:${festival.venue || ''}, ${festival.address || festival.location}
DESCRIPTION:${festival.description}
END:VEVENT
END:VCALENDAR`;
  const icsUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;

  // Outlook
  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startDate}&enddt=${endDate}&location=${location}&body=${description}`;

  return { googleUrl, icsUrl, outlookUrl };
};

interface FestivalDetailViewProps {
  festival: Festival;
  onBack: () => void;
  onViewMap: () => void;
  onSelectVendor?: (vendor: Vendor) => void;
  savedVendorIds?: string[];
  onToggleVendorBookmark?: (vendorId: string) => void;
  isCheckedIn?: boolean;
  onCheckIn?: (note?: string) => void;
  festivalNotes?: FestivalNote[];
  onAddNote?: (note: Omit<FestivalNote, 'id' | 'timestamp' | 'festivalId'>) => void;
  onDeleteNote?: (noteId: string) => void;
}

export const FestivalDetailView: React.FC<FestivalDetailViewProps> = ({
  festival,
  onBack,
  onViewMap,
  onSelectVendor,
  savedVendorIds = [],
  onToggleVendorBookmark,
  isCheckedIn = false,
  onCheckIn,
  festivalNotes = [],
  onAddNote,
  onDeleteNote,
}) => {
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const vendors = SAMPLE_VENDORS.filter((v) => v.festivalId === festival.id);
  const featuredVendors = vendors.filter((v) => v.featured);
  const savedVendors = vendors.filter((v) => savedVendorIds.includes(v.id));
  const calendarLinks = generateCalendarLinks(festival);

  return (
    <div className="flex-1 flex flex-col h-full bg-stone-900">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
        <div className="flex items-center gap-2 px-4 py-3">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <span className="text-white font-semibold">{festival.shortName}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-b from-amber-600/20 to-stone-900 px-6 py-8">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{festival.name}</h1>
            <p className="text-white/70 mb-4">{festival.description}</p>

            {/* Quick Info Cards - Tappable */}
            <div className="grid grid-cols-2 gap-3 mt-6">
              {festival.date && (
                <button
                  onClick={() => setShowCalendarModal(true)}
                  className="bg-white/5 rounded-xl p-4 border border-white/10 text-left hover:bg-white/10 hover:border-amber-500/30 transition-colors"
                >
                  <Calendar className="w-5 h-5 text-amber-500 mb-2" />
                  <p className="text-white font-medium text-sm">{festival.date}</p>
                  {festival.time && <p className="text-white/50 text-xs mt-1">{festival.time}</p>}
                </button>
              )}

              <button
                onClick={onViewMap}
                className="bg-white/5 rounded-xl p-4 border border-white/10 text-left hover:bg-white/10 hover:border-amber-500/30 transition-colors"
              >
                <MapPin className="w-5 h-5 text-amber-500 mb-2" />
                <p className="text-white font-medium text-sm">{festival.location}</p>
                {festival.venue && <p className="text-white/50 text-xs mt-1">{festival.venue}</p>}
              </button>
            </div>

            {/* Admission Info */}
            {festival.admissionInfo && (
              <div className="mt-4 flex items-center gap-2 text-sm">
                <Ticket className="w-4 h-4 text-amber-500" />
                <span className="text-white/70">{festival.admissionInfo}</span>
              </div>
            )}
          </div>
        </div>

        {/* Highlights */}
        {festival.highlights && festival.highlights.length > 0 && (
          <div className="px-6 py-6 border-b border-white/10">
            <h2 className="text-lg font-semibold text-white mb-4">What to Expect</h2>
            <div className="space-y-3">
              {festival.highlights.map((highlight, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center mt-0.5">
                    <Check className="w-3 h-3 text-amber-500" />
                  </div>
                  <span className="text-white/80 text-sm">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured Vendors */}
        {featuredVendors.length > 0 && (
          <div className="px-6 py-6 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Featured Vendors</h2>
              <span className="text-white/50 text-sm">{vendors.length} total</span>
            </div>

            <div className="space-y-3">
              {featuredVendors.slice(0, 4).map((vendor) => (
                <button
                  key={vendor.id}
                  onClick={() => onSelectVendor?.(vendor)}
                  className="w-full flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-left"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                    <Star className="w-5 h-5 text-stone-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium text-sm truncate">{vendor.name}</h3>
                      {vendor.boothNumber && (
                        <span className="text-amber-500 text-xs">Booth {vendor.boothNumber}</span>
                      )}
                    </div>
                    <p className="text-white/50 text-xs mt-0.5 line-clamp-1">{vendor.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {vendor.brands.slice(0, 3).map((brand) => (
                        <span
                          key={brand}
                          className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/70"
                        >
                          {brand}
                        </span>
                      ))}
                      {vendor.brands.length > 3 && (
                        <span className="px-2 py-0.5 rounded text-xs bg-white/5 text-white/40">
                          +{vendor.brands.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Saved Vendors */}
        {savedVendors.length > 0 && (
          <div className="px-6 py-6 border-b border-white/10 bg-amber-500/5">
            <div className="flex items-center gap-2 mb-4">
              <BookMarked className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-white">My Saved Vendors</h2>
              <span className="text-white/50 text-sm">({savedVendors.length})</span>
            </div>
            <div className="space-y-2">
              {savedVendors.map((vendor) => (
                <div
                  key={vendor.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-amber-500/10 border border-amber-500/30"
                >
                  <button
                    onClick={() => onSelectVendor?.(vendor)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm">{vendor.name}</span>
                      {vendor.boothNumber && (
                        <span className="text-amber-500 text-xs">Booth {vendor.boothNumber}</span>
                      )}
                    </div>
                  </button>
                  {onToggleVendorBookmark && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleVendorBookmark(vendor.id); }}
                      className="p-2 rounded-lg hover:bg-amber-500/20 transition-colors"
                    >
                      <BookMarked className="w-4 h-4 text-amber-500" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Vendors List */}
        <div className="px-6 py-6 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white mb-4">All Vendors</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {vendors.map((vendor) => {
              const isSaved = savedVendorIds.includes(vendor.id);
              return (
                <div
                  key={vendor.id}
                  className={`relative p-3 rounded-xl border text-left transition-colors ${
                    vendor.featured
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : isSaved
                        ? 'bg-amber-500/5 border-amber-500/20'
                        : 'bg-white/5 border-white/10'
                  }`}
                >
                  <button
                    onClick={() => onSelectVendor?.(vendor)}
                    className="w-full text-left"
                  >
                    <div className="flex items-center gap-2 mb-1 pr-6">
                      {vendor.featured && <Star className="w-3 h-3 text-amber-500" />}
                      <span className="text-white font-medium text-xs truncate">{vendor.name}</span>
                    </div>
                    {vendor.boothNumber && (
                      <span className="text-white/40 text-xs">Booth {vendor.boothNumber}</span>
                    )}
                  </button>
                  {onToggleVendorBookmark && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleVendorBookmark(vendor.id); }}
                      className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 transition-colors"
                    >
                      {isSaved ? (
                        <BookMarked className="w-4 h-4 text-amber-500" />
                      ) : (
                        <Bookmark className="w-4 h-4 text-white/30 hover:text-white/60" />
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* My Notes Section */}
        {(festivalNotes.length > 0 || onAddNote) && (
          <div className="px-6 py-6 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PenLine className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-white">My Notes</h2>
                {festivalNotes.length > 0 && (
                  <span className="text-white/50 text-sm">({festivalNotes.length})</span>
                )}
              </div>
              {onAddNote && (
                <button
                  onClick={() => setShowAddNoteModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-500 text-xs font-medium hover:bg-amber-500/30 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Note
                </button>
              )}
            </div>

            {festivalNotes.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-4">
                No notes yet. Record cigars you try at each booth!
              </p>
            ) : (
              <div className="space-y-3">
                {festivalNotes.map((note) => (
                  <div
                    key={note.id}
                    className="p-3 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {note.cigarTried && (
                          <p className="text-amber-500 text-xs font-medium mb-1">{note.cigarTried}</p>
                        )}
                        {note.vendorName && (
                          <p className="text-white/60 text-xs mb-1">@ {note.vendorName}</p>
                        )}
                        <p className="text-white text-sm">{note.content}</p>
                        <p className="text-white/30 text-xs mt-2">
                          {new Date(note.timestamp).toLocaleString()}
                        </p>
                      </div>
                      {onDeleteNote && (
                        <button
                          onClick={() => onDeleteNote(note.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Location */}
        <div className="px-6 py-6">
          <h2 className="text-lg font-semibold text-white mb-4">Location</h2>
          <div className="bg-white/5 rounded-xl border border-white/10 p-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">{festival.venue || festival.location}</p>
                {festival.address && <p className="text-white/60 text-sm mt-1">{festival.address}</p>}
              </div>
            </div>

            {/* Mini Map Preview */}
            <button
              onClick={onViewMap}
              className="w-full mt-4 h-32 rounded-lg bg-stone-800 border border-white/10 flex items-center justify-center gap-2 hover:bg-stone-700 transition-colors"
            >
              <MapIcon className="w-5 h-5 text-white/50" />
              <span className="text-white/50">View Interactive Map</span>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-6 pb-8 space-y-3">
          {/* Check-in button */}
          {onCheckIn && (
            isCheckedIn ? (
              <div className="w-full py-3 px-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 font-medium flex items-center justify-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Checked In Today!
              </div>
            ) : (
              <button
                onClick={() => setShowCheckInModal(true)}
                className="w-full py-3 px-4 rounded-xl bg-green-500 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-400 transition-colors"
              >
                <Navigation className="w-5 h-5" />
                Check In Here
              </button>
            )
          )}

          <button
            onClick={onViewMap}
            className="w-full py-3 px-4 rounded-xl bg-amber-500 text-stone-900 font-semibold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors"
          >
            <MapIcon className="w-5 h-5" />
            View Festival Map
          </button>

          {festival.date && (
            <button
              onClick={() => setShowCalendarModal(true)}
              className="w-full py-3 px-4 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
            >
              <CalendarPlus className="w-5 h-5" />
              Add to Calendar
            </button>
          )}

          {festival.website && (
            <a
              href={festival.website}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-4 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
            >
              <ExternalLink className="w-5 h-5" />
              Official Website
            </a>
          )}
        </div>
      </div>

      {/* Calendar Modal */}
      {showCalendarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowCalendarModal(false)} />
          <div className="relative bg-stone-800 rounded-2xl p-6 max-w-sm w-full border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">Add to Calendar</h3>
            <p className="text-white/60 text-sm mb-4">{festival.name}</p>

            <div className="space-y-2">
              <a
                href={calendarLinks.googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 px-4 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
                onClick={() => setShowCalendarModal(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.5 3h-3V1.5h-1.5V3h-6V1.5H7.5V3h-3C3.675 3 3 3.675 3 4.5v15c0 .825.675 1.5 1.5 1.5h15c.825 0 1.5-.675 1.5-1.5v-15c0-.825-.675-1.5-1.5-1.5zm0 16.5h-15V9h15v10.5zm0-12h-15V4.5h15V7.5z"/>
                </svg>
                Google Calendar
              </a>

              <a
                href={calendarLinks.icsUrl}
                download={`${festival.shortName || festival.name}.ics`}
                className="w-full py-3 px-4 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
                onClick={() => setShowCalendarModal(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 2H7C5.9 2 5 2.9 5 4v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-5 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm5-3H7V4h10v13z"/>
                </svg>
                Apple Calendar / iOS
              </a>

              <a
                href={calendarLinks.outlookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 px-4 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
                onClick={() => setShowCalendarModal(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"/>
                </svg>
                Outlook
              </a>
            </div>

            <button
              onClick={() => setShowCalendarModal(false)}
              className="w-full mt-4 py-2.5 border border-white/20 text-white/70 font-medium rounded-xl hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Check-in Modal */}
      {showCheckInModal && onCheckIn && (
        <CheckInModal
          festivalName={festival.name}
          onConfirm={(note) => {
            onCheckIn(note);
            setShowCheckInModal(false);
          }}
          onCancel={() => setShowCheckInModal(false)}
        />
      )}

      {/* Add Note Modal */}
      {showAddNoteModal && onAddNote && (
        <AddNoteModal
          vendors={vendors}
          onConfirm={(note) => {
            onAddNote(note);
            setShowAddNoteModal(false);
          }}
          onCancel={() => setShowAddNoteModal(false)}
        />
      )}
    </div>
  );
};

// Check-in Modal Component
const CheckInModal: React.FC<{
  festivalName: string;
  onConfirm: (note?: string) => void;
  onCancel: () => void;
}> = ({ festivalName, onConfirm, onCancel }) => {
  const [note, setNote] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onCancel} />
      <div className="relative bg-stone-800 rounded-2xl p-6 max-w-sm w-full border border-white/10">
        <div className="text-4xl mb-4 text-center">🎉</div>
        <h3 className="text-lg font-semibold text-white mb-2 text-center">Check In</h3>
        <p className="text-white/60 text-sm mb-4 text-center">{festivalName}</p>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (optional) - What are you most excited about?"
          className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm resize-none focus:outline-none focus:border-amber-500/50"
          rows={3}
        />

        <div className="flex gap-3 mt-4">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 border border-white/20 text-white/70 font-medium rounded-xl hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(note || undefined)}
            className="flex-1 py-2.5 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-400 transition-colors"
          >
            Check In!
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Note Modal Component
const AddNoteModal: React.FC<{
  vendors: Vendor[];
  onConfirm: (note: Omit<FestivalNote, 'id' | 'timestamp' | 'festivalId'>) => void;
  onCancel: () => void;
}> = ({ vendors, onConfirm, onCancel }) => {
  const [vendorId, setVendorId] = useState('');
  const [cigarTried, setCigarTried] = useState('');
  const [content, setContent] = useState('');

  const selectedVendor = vendors.find(v => v.id === vendorId);

  const handleSubmit = () => {
    if (!content.trim()) return;
    onConfirm({
      vendorId: vendorId || undefined,
      vendorName: selectedVendor?.name,
      cigarTried: cigarTried.trim() || undefined,
      content: content.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onCancel} />
      <div className="relative bg-stone-800 rounded-2xl p-6 max-w-sm w-full border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Add Note</h3>

        {/* Vendor selector */}
        <label className="block text-white/60 text-sm mb-2">Vendor (optional)</label>
        <select
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm mb-4 focus:outline-none focus:border-amber-500/50"
        >
          <option value="">Select a vendor...</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>

        {/* Cigar name */}
        <label className="block text-white/60 text-sm mb-2">Cigar Tried (optional)</label>
        <input
          type="text"
          value={cigarTried}
          onChange={(e) => setCigarTried(e.target.value)}
          placeholder="e.g., Arturo Fuente Hemingway"
          className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm mb-4 focus:outline-none focus:border-amber-500/50"
        />

        {/* Note content */}
        <label className="block text-white/60 text-sm mb-2">Your Notes</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Great flavor, hints of coffee and dark chocolate. Would buy a box!"
          className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm resize-none focus:outline-none focus:border-amber-500/50"
          rows={4}
        />

        <div className="flex gap-3 mt-4">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 border border-white/20 text-white/70 font-medium rounded-xl hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!content.trim()}
            className="flex-1 py-2.5 bg-amber-500 text-stone-900 font-semibold rounded-xl hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  );
};

export default FestivalDetailView;
